import { browser } from '$app/environment';
import { untrack } from 'svelte';
import { Controller } from './Controller.svelte';
import { AutoScroll, type ScrollOptions } from './modifiers/AutoScroll';
import { Controls, type Directions } from './modifiers/Controls';
import { Drag, type DragOptions } from './modifiers/Drag';
import type { Modifier } from './modifiers/Modifier';
import { Snap, SnapAlign, type SnapOptions } from './modifiers/Snap';
import { Translator } from './modifiers/Translator';

type StyleObject = Record<string, number | string | undefined>;
function styleToString(style: StyleObject): string {
	return Object.keys(style).reduce((str, key) => {
		if (style[key] === undefined) return str;
		return str + `${key}:${style[key]};`;
	}, '');
}

export interface Context<T> {
	state: State<T>;
	connection: Connection;
	prop: Attributes;
}

export class State<T> {
	public gap = $state(0);
	public drag = $state(0);
	public items = $state<CarouselItem<T>[]>([]);
	public roundPixels = $state(false);
	public align: SnapAlign = $state(SnapAlign.START);
	public containerWidth = $state(0);

	public totalSize = $derived(
		this.items.reduce((current, next) => current + next.width + this.gap, 0)
	);

	// The drag amount bounded to the total size. Number between 0 and totalSize.
	public boundedDrag = $derived.by(() => {
		return this.calculateRoundingDrag(this.drag);
	});

	public index = $derived.by(() => {
		if (isNaN(this.boundedDrag)) return 0;
		return this.getClosestIndex(this.boundedDrag);
	});

	public getStaticOffset(index: number) {
		if (index >= this.items.length) return this.totalSize;
		let offset = 0;
		for (let i = 0; i < index; i++) {
			offset += this.items[i].width + this.gap;
		}
		return offset;
	}

	/**
	 * Always returns the shortest offset to the index in a given direction
	 **/
	public getOffsetToIndex(
		index: number,
		direction: 'left' | 'right' | 'auto' = 'auto',
		targetDrag?: number
	) {
		index = index % this.items.length;
		if (index < 0) index = this.items.length + index;
		const target = this.items[index];
		if (!target) return 0;

		targetDrag = this.calculateRoundingDrag(targetDrag);

		const drag = this.totalSize - targetDrag;
		let offset = drag - target.staticPos;
		if (direction === 'auto') {
			if (Math.abs(offset) > this.totalSize / 2) {
				// always take the shortest route
				offset = offset - this.totalSize;
			}
		} else if (direction === 'right') {
			while (offset < 0) {
				offset = offset + this.totalSize;
			}
		} else if (direction === 'left') {
			while (offset > 0) {
				offset = offset - this.totalSize;
			}
		}
		return offset;
	}

	public roundToIndex(dragAmount: number) {
		const index = this.getClosestIndex(dragAmount);
		const offset = this.getOffsetToIndex(index, 'auto', dragAmount);
		return dragAmount + offset;
	}

	public offsetToCurrent = $derived.by(() => {
		return this.getOffsetToIndex(this.index);
	});

	public offsetToNext = $derived.by(() => {
		return this.getOffsetToIndex(this.index + 1);
	});

	public offsetToPrev = $derived.by(() => {
		return this.getOffsetToIndex(this.index - 1);
	});

	public getClosestIndex(targetDrag?: number) {
		const dragAmount = this.calculateRoundingDrag(targetDrag);

		let index = 0;
		this.items.forEach((item) => {
			let indexPointer = Math.ceil(this.totalSize - dragAmount);
			indexPointer += 100;
			if (
				indexPointer >= item.staticPos - this.gap * 0.5 &&
				indexPointer <= item.staticPos + item.width + this.gap * 0.5
			) {
				index = item.index;
				return;
			}
		});
		return index;
	}

	private calculateRoundingDrag(targetDrag?: number) {
		targetDrag = targetDrag ?? this.drag;
		let target = targetDrag % this.totalSize;
		if (target <= 0) {
			target += this.totalSize;
		}
		return target;
	}
}

class Attributes {
	public cursor = $state('pointer');
	public touchAction = $state('unset');

	public enabled = $state(false);

	public height = $state(0);
	public setHeight = $state(false);

	public get container() {
		if (!this.enabled) return {};
		return {
			style: styleToString({
				height: this.setHeight ? `${this.height}px` : undefined,
				'touch-action': this.touchAction,
				position: 'relative',
				cursor: this.cursor
			})
		};
	}
}

export class Connection {
	public containerElement?: HTMLElement;

	/**
	 * Svelte action to bind the container element to the controller
	 */
	public readonly container = (node: HTMLElement) => {
		this.containerElement = node;
	};
}

interface CarouselOptions {
	autoRun: boolean;
	friction: number;
	setHeightToTallestItem: boolean;
	mode: 'endless' | 'boxed' | 'auto'; // Auto is still on the todo
	snapPixels: boolean;
	scroll?: Partial<ScrollOptions> | boolean;
	drag?: Partial<DragOptions> | boolean;
	snap?: Partial<SnapOptions> | boolean;
}

export class CarouselItem<T> {
	public readonly data: T;
	public _node?: HTMLElement;
	public get node() {
		if (!this._node) throw new Error('Node not set');
		return this._node;
	}

	private stylePosition: string | undefined = $state(undefined);
	private styleOpacity: number | undefined = $state(0);

	public index = $state(-1);
	private readonly state: State<T>;

	public setItemHeight = $state(false);
	public onResize = () => {};

	public staticPos: number = $state(0);
	public pos: number = $state(0);
	public width: number = $state(0);
	public height: number = $state(0);

	public enabled = $state(false);

	private readonly resizeObserver!: ResizeObserver;

	constructor(data: T, state: State<T>) {
		this.data = data;
		this.state = state;

		if (browser) {
			this.resizeObserver = new ResizeObserver((_) => {
				this.onResize();
			});
		}
	}

	public isValid() {
		return !!this._node;
	}

	public useItem = (node: HTMLElement) => {
		this._node = node;

		const allImgs = node.getElementsByTagName('img');
		for (let i = 0; i < allImgs.length; i++) {
			const img = allImgs[i];
			img.setAttribute('draggable', 'false');
		}

		this.stylePosition = 'absolute';
		this.styleOpacity = undefined;
		this.width = node.offsetWidth;

		this.redraw();
		this.pos = this.state.getStaticOffset(this.index);
		this.resizeObserver.observe(node);
	};

	public moveTo(pos: number) {
		this.pos = pos;
	}

	public get attr() {
		if (!this.enabled) return {};

		let targetPos = this.pos;
		if (this.state.roundPixels) targetPos = Math.round(targetPos);

		return {
			style: styleToString({
				position: this.stylePosition,
				opacity: this.styleOpacity,
				transform: `translateX(${targetPos}px)`,
				height: this.setItemHeight ? `${this.height}px` : undefined,
				'user-drag': 'none',
				'user-select': 'none',
				'-webkit-user-drag': 'none',
				'-webkit-user-select': 'none',
				'-moz-user-select': 'none',
				'-ms-user-select': 'none'
			}),
			'data-index': this.index
		};
	}

	public preRedraw() {
		this.node.style.removeProperty('height');
	}

	public redraw() {
		this.width = this.node.offsetWidth;
		this.height = this.node.offsetHeight;
	}

	public postRedraw() {
		this.staticPos = this.state.getStaticOffset(this.index);

		// force update the height after setting it to null
		if (this.setItemHeight) {
			this.node.style.height = `${this.height}px`;
		} else {
			this.node.style.removeProperty('height');
		}
	}
}

export class Carousel<T> {
	public readonly state: Omit<State<T>, 'setSize'>;
	public readonly connection: Connection;
	public readonly attr: Attributes = new Attributes();

	public get items() {
		return this.state.items;
	}

	private options: CarouselOptions;

	private readonly controls = new Controls();
	private readonly controller;
	private isStarted: boolean = false;
	private animationFrameIndex: number = -1;

	public static from<T>(data: T[], options: Partial<CarouselOptions> = {}) {
		const instance = new Carousel<T>(options);
		data.forEach((item) => {
			const accordionItem = new CarouselItem(item, instance.state as State<T>);
			instance.addItem(accordionItem);
		});
		return instance;
	}

	constructor(options?: Partial<CarouselOptions>) {
		this.options = {
			autoRun: true,
			setHeightToTallestItem: false,
			friction: 0.7,
			snapPixels: false,
			mode: 'endless',
			...options
		};

		this.state = new State();
		this.state.roundPixels = this.options.snapPixels;
		this.connection = new Connection();
		this.controller = new Controller({
			state: this.state as State<T>,
			connection: this.connection,
			prop: this.attr
		});

		this.controller.main.friction = this.options.friction;

		// Add modifiers
		this.controller.add(this.controls);

		if (options?.drag) {
			if (typeof options.drag === 'boolean') this.controller.add(new Drag({}));
			else this.controller.add(new Drag(options.drag));
		}
		if (options?.scroll) {
			if (typeof options.scroll === 'boolean') this.controller.add(new AutoScroll({}));
			else this.controller.add(new AutoScroll(options.scroll));
		}
		if (options?.snap) {
			if (typeof options.snap === 'boolean') this.controller.add(new Snap({}));
			else this.controller.add(new Snap(options.snap));
		}

		this.controller.add(new Translator({ mode: this.options.mode }));

		$effect(() => {
			if (this.options.autoRun) {
				untrack(() => this.start());
			} else {
				untrack(() => this.stop());
			}
			return () => {
				untrack(() => this.stop());
			};
		});
	}

	public addItem(item: CarouselItem<T>) {
		this.state.items.push(item);
		item.onResize = this.redraw;
	}

	public start() {
		if (this.isStarted) return;
		this.isStarted = true;
		this.attr.enabled = true;
		this.items.forEach((item) => (item.enabled = true));

		if (browser) {
			window.addEventListener('resize', this.redraw);
			window.addEventListener('visibilitychange', this.startOrStop);
			this.startOrStop();
		}
	}

	private startOrStop = () => {
		if (!this.isStarted) return;

		if (document.hidden) {
			this.controller.stop();
		} else {
			this.controller.start();
			this.tick();
		}
	};

	public stop() {
		if (!this.isStarted) return;
		window.cancelAnimationFrame(this.animationFrameIndex);
		this.isStarted = false;

		this.attr.enabled = false;
		this.items.forEach((item) => (item.enabled = false));
		this.controller.stop();

		if (browser) {
			window.removeEventListener('resize', this.redraw);
			window.removeEventListener('visibilitychange', this.startOrStop);
		}
	}

	private tick = () => {
		window.cancelAnimationFrame(this.animationFrameIndex);
		this.animationFrameIndex = window.requestAnimationFrame(this.tick);

		this.controller.tick();

		// BUG:
		// As of this writing, simply logging a rune in this function breaks the carousel
		// This is a bug in svelte 5, so we are waiting for the next version to fix this
		// Till then we use the effect in start() and call the translateOnEffect instead
		// TODO:
		// use this instead of the translateOnEffect in the start method
		// this.translator.translate();
	};

	public redraw = () => {
		if (!this.isStarted) return;
		this.state.items = this.state.items.filter((item) => item.isValid());
		this.state.items.forEach((item, index) => {
			item.index = index;
		});

		this.connection.containerElement!.style.removeProperty('height');
		this.items.forEach((item) => {
			item.preRedraw();
		});

		this.state.items.forEach((item) => {
			item.redraw();
		});

		if (this.options.setHeightToTallestItem) {
			const largestItem = this.items.reduce((current, next) => {
				if (current.height > next.height) return current;
				return next;
			});
			this.attr.height = largestItem.height;
			this.attr.setHeight = true;
			this.connection.containerElement!.style.height = `${largestItem.height}px`;
			this.items.forEach((item) => {
				item.setItemHeight = true;
				item.height = largestItem.height;
			});
		}

		this.items.forEach((item) => {
			item.postRedraw();
		});

		this.state.containerWidth = this.connection.containerElement?.offsetWidth ?? 0;

		const snap = this.getModifier(Snap);
		if (snap) {
			const offset = snap.snap();
			this.state.drag = offset;
		}
	};

	public addModifier(modifier: Modifier) {
		this.controller.add(modifier);
	}

	public getModifier<T extends Modifier>(type: new (...args: any[]) => T): T | undefined {
		return this.controller.get(type);
	}

	public useContainer = (node: HTMLElement) => {
		this.connection.container(node);
		this.redraw();
	};

	public goTo(index: number, direction: Directions = 'auto') {
		untrack(() => {
			this.controls.goto(index, direction);
		});
	}

	public next = () => {
		untrack(() => {
			this.controls.gotoNext();
		});
	};
	public prev = () => {
		untrack(() => {
			this.controls.gotoPrev();
		});
	};
}
